import { ResourceName, Task, ObjectType, TaskStatus, TaskRecordReference } from "@/types";
import { Box, CircularProgress, Typography, Stack, Divider, Button } from "@mui/material";
import { parseISO } from "date-fns";
import { useMemo, useState } from "react";
import { Identifier, useGetList } from "react-admin";
import { TaskListItem } from "./TaskListItem";

export interface CompactTaskListProps {
  tasks?: Task[];
  isLoading?: boolean;
  recordRef?: TaskRecordReference;
  statusFilter?: TaskStatus | "all";
  limit?: number;
  onTaskClick?: (task: Task) => void;
  hideDeleted?: boolean;
  hideCompleted?: boolean;
  excludeAssignedUserId?: Identifier;
  showAssignedUser?: boolean;
  showRelatedTo?: boolean;
  showCheckbox?: boolean;
  resource?: string | ResourceName;
  enablePagination?: boolean;
  initialPerPage?: number;
}

export const CompactTaskList = ({
  tasks: externalTasks,
  isLoading: externalLoading,
  resource = ResourceName.TASKS,
  recordRef,
  statusFilter = "all",
  limit,
  onTaskClick,
  hideDeleted = true,
  hideCompleted = false,
  excludeAssignedUserId,
  showAssignedUser = true,
  showRelatedTo = true,
  showCheckbox = true,
  enablePagination = false,
  initialPerPage = 25,
}: CompactTaskListProps) => {
  const [page, setPage] = useState(1);
  const [perPage] = useState(initialPerPage);

  const shouldFetch = !externalTasks;

  const serverFilter = useMemo(() => {
    if (!shouldFetch) return {};

    const filter: Record<string, TaskStatus | boolean | ObjectType | Identifier | string> = {};

    if (statusFilter !== "all") filter.status = statusFilter;
    if (hideDeleted) filter.deleted = false;
    if (hideCompleted) filter.completed = false;
    if (recordRef) {
      if (recordRef.object_type) filter["record.object_type"] = recordRef.object_type;
      if (recordRef.object_id) filter["record.object_id"] = recordRef.object_id;
    }

    return filter;
  }, [shouldFetch, statusFilter, hideDeleted, hideCompleted, recordRef]);

  const {
    data: fetchedTasks = [],
    isLoading: isFetching,
    error,
  } = useGetList<Task>(
    resource,
    {
      filter: serverFilter,
      pagination: { page: 1, perPage: limit || 500 },
      sort: { field: "due_date", order: "ASC" },
    },
    { enabled: shouldFetch }
  );

  const taskData = externalTasks ?? fetchedTasks;
  const isLoading = externalLoading ?? isFetching;

  const items = useMemo(() => {
    let filtered = [...taskData];

    if (hideDeleted) filtered = filtered.filter((t) => !t.deleted);
    if (hideCompleted) filtered = filtered.filter((t) => !t.completed);
    if (statusFilter !== "all") filtered = filtered.filter((t) => t.status === statusFilter);

    if (recordRef) {
      filtered = filtered.filter(
        (t) =>
          t.record &&
          String(t.record.object_type) === String(recordRef.object_type) &&
          String(t.record.object_id) === String(recordRef.object_id)
      );
    }

    if (excludeAssignedUserId) {
      filtered = filtered.filter((t) => String(t.assigned_user_id_ref) !== String(excludeAssignedUserId));
    }

    filtered.sort((a, b) => {
      const da = a.due_date ? parseISO(a.due_date) : null;
      const db = b.due_date ? parseISO(b.due_date) : null;
      if (da && db) return +da - +db;
      if (da && !db) return -1;
      if (!da && db) return 1;
      return 0;
    });

    return limit ? filtered.slice(0, limit) : filtered;
  }, [taskData, hideDeleted, hideCompleted, statusFilter, recordRef, excludeAssignedUserId, limit]);

  const paginatedItems = enablePagination ? items.slice((page - 1) * perPage, page * perPage) : items;

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">
            Loading tasks...
          </Typography>
        </Stack>
      </Box>
    );
  }

  if (error && shouldFetch) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <Typography variant="body2" color="error">
          Unable to load tasks.
        </Typography>
      </Box>
    );
  }

  if (items.length === 0) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <Typography variant="body2" color="text.secondary">
          No tasks
        </Typography>
      </Box>
    );
  }

  return (
    <Stack divider={<Divider />}>
      {paginatedItems.map((task) => (
        <TaskListItem
          key={task.id}
          task={task}
          showAssignedUser={showAssignedUser}
          showRelatedTo={showRelatedTo}
          showCheckbox={showCheckbox}
          onTaskClick={onTaskClick}
        />
      ))}

      {enablePagination && items.length > perPage && (
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 2, py: 1.5 }}>
          <Typography variant="caption" color="text.secondary">
            {(page - 1) * perPage + 1}–{Math.min(page * perPage, items.length)} of {items.length}
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button size="small" disabled={page === 1} onClick={() => setPage(page - 1)}>
              Previous
            </Button>
            <Button size="small" disabled={page * perPage >= items.length} onClick={() => setPage(page + 1)}>
              Next
            </Button>
          </Stack>
        </Stack>
      )}
    </Stack>
  );
};
